import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, RotateCcw, Download, Award } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { downloadFile } from "@/lib/training";
import { formatDate } from "@/lib/constants";
import { EmptyState } from "@/components/Badges";

function Attempts({ attempts }) {
  if (!attempts.length) return null;
  const best = Math.max(...attempts.map((a) => a.score));
  return (
    <section className="mt-12" data-testid="quiz-attempts">
      <h2 className="font-display text-sm font-bold uppercase tracking-[0.14em]">Your attempts</h2>
      <p className="mt-2 text-sm text-muted-foreground" data-testid="quiz-best-last">Best {best}% · Last {attempts[0].score}%</p>
      <div className="mt-4 divide-y divide-lasso-line rounded-2xl border border-lasso-line bg-white">
        {attempts.map((a, i) => (
          <div key={a.id} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`quiz-attempt-${attempts.length - i}`}>
            <span>Attempt {attempts.length - i} · {formatDate(a.created_at)}</span>
            <span className={`font-semibold ${a.passed ? "text-emerald-700" : "text-rose-600"}`}>{a.score}% · {a.passed ? "Passed" : "Not passed"}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function CourseQuiz() {
  const { id } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadAttempts = useCallback(() => api.get(`/courses/${id}/attempts`).then((r) => setAttempts(r.data)), [id]);

  useEffect(() => {
    api.get(`/courses/${id}/quiz`).then((r) => setQuiz(r.data)).catch((e) => setError(formatApiError(e)));
    loadAttempts();
  }, [id, loadAttempts]);

  const submit = async () => {
    if (Object.keys(answers).length < quiz.questions.length) return toast.error("Answer every question before submitting");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/courses/${id}/quiz/submit`, { answers, draw_id: quiz.draw_id });
      setResult(data);
      loadAttempts();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => {
    setResult(null);
    setAnswers({});
    api.get(`/courses/${id}/quiz`).then((r) => setQuiz(r.data)).catch((e) => setError(formatApiError(e)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (error) return <EmptyState title="Quiz locked" description={error} testId="quiz-locked" />;
  if (!quiz) return null;
  const wrong = new Set(result?.wrong_question_ids || []);

  return (
    <div className="fade-up mx-auto max-w-3xl">
      <Link to={`/courses/${id}`} className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-lasso-ink" data-testid="quiz-back-link"><ArrowLeft className="h-4 w-4" /> Course overview</Link>
      <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">Final quiz</h1>
      <p className="mt-2 text-muted-foreground">{quiz.questions.length} questions{quiz.bank_size > quiz.questions.length ? ` drawn at random from a bank of ${quiz.bank_size}` : ""} · pass mark {quiz.pass_mark}% · unlimited retakes</p>

      {result && (
        <div className={`mt-8 rounded-3xl p-8 ${result.passed ? "bg-lasso-ink text-white" : "border border-rose-200 bg-rose-50"}`} data-testid="quiz-result">
          <div className="flex items-center gap-3">
            {result.passed ? <Award className="h-6 w-6 text-lasso-sky" /> : <XCircle className="h-6 w-6 text-rose-600" />}
            <p className="font-display text-lg font-bold" data-testid="quiz-result-status">{result.passed ? "You passed!" : "Not quite — review and try again"}</p>
          </div>
          <p className="mt-4 font-display text-5xl font-bold" data-testid="quiz-result-score">{result.score}%</p>
          <p className={`mt-2 text-sm ${result.passed ? "text-white/70" : "text-rose-800"}`}>{result.correct} of {result.total} correct · pass mark {result.pass_mark}%</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {result.passed && result.certificate_code && (
              <button onClick={() => downloadFile(`/certificates/${result.certificate_code}/pdf`, `Lasso-Hub-${result.certificate_code}.pdf`)} className="inline-flex items-center gap-2 rounded-full bg-lasso-sky px-5 py-2.5 text-sm font-semibold text-lasso-ink" data-testid="quiz-certificate-download">
                <Download className="h-4 w-4" /> Download certificate
              </button>
            )}
            <button onClick={retake} className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold ${result.passed ? "border-white/30" : "border-rose-300 bg-white"}`} data-testid="quiz-retake-button">
              <RotateCcw className="h-4 w-4" /> Retake quiz
            </button>
          </div>
        </div>
      )}

      <ol className="mt-10 space-y-5">
        {quiz.questions.map((q, i) => (
          <li key={q.id} className={`rounded-2xl border bg-white p-6 ${result ? (wrong.has(q.id) ? "border-rose-300" : "border-emerald-300") : "border-lasso-line"}`} data-testid={`quiz-question-${i + 1}`}>
            <div className="flex items-start justify-between gap-4">
              <p className="font-semibold"><span className="mr-2 font-mono text-xs text-lasso-deep">Q{i + 1}</span>{q.prompt}</p>
              {result && (wrong.has(q.id)
                ? <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-rose-600" data-testid={`quiz-question-${i + 1}-wrong`}><XCircle className="h-4 w-4" /> Incorrect</span>
                : <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`quiz-question-${i + 1}-correct`}><CheckCircle2 className="h-4 w-4" /> Correct</span>)}
            </div>
            <div className="mt-4 grid gap-2">
              {q.options.map((o, oi) => (
                <label key={oi} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${answers[q.id] === oi ? "border-lasso-ink bg-lasso-tint" : "border-lasso-line hover:border-lasso-sky"} ${result ? "pointer-events-none" : ""}`}>
                  <input type="radio" name={q.id} className="accent-black" checked={answers[q.id] === oi} onChange={() => setAnswers({ ...answers, [q.id]: oi })} data-testid={`quiz-question-${i + 1}-option-${oi}`} />
                  {o}
                </label>
              ))}
            </div>
          </li>
        ))}
      </ol>
      {!result && (
        <button onClick={submit} disabled={submitting} className="mt-8 w-full rounded-full bg-lasso-ink px-6 py-3.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="quiz-submit-button">
          Submit answers
        </button>
      )}
      <Attempts attempts={attempts} />
    </div>
  );
}
